import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import moment from 'moment';

import { selectors as userSelectors } from '@elab/core/store/user';
import {
    actions as cartActions,
    selectors as cartSelectors,
    types as cartTypes,
} from '@elab/core/store/cart';
import {
    actions as checkoutActions,
    selectors as checkoutSelectors,
    types as checkoutTypes,
} from '@elab/core/store/checkout';
import dynaForms from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import { actions as uiActions, types as uiTypes } from '@elab/core/store/ui';
import { actions as globalActions, selectors as globalSelectors } from '@elab/core/store/global';
import constants from '@elab/core/utils/constants';
import { parseDeliveryTimeSlot } from '@elab/core/utils/DateUtils';
import { selectors as configSelectors } from '@elab/core/store/config';
import analytics from '@elab/core/utils/analytics';
import { createDeliveryModesLoader } from '@elab/components/connected/DeliveryModes/redux/saga';
import { putDeliveryAddress } from '@elab/components/connected/customerAddresses/redux/saga';
import { setRegisteredOnCheckout } from '@elab/core/utils/authCookiesManager';
import {
    copySecondAddressFields,
    initAddressSuggestionWatchers,
} from '@elab/core/utils/address/sagaHelpers';

import { api } from './api';
import actions from './actions';
import selectors from './selectors';
import types from './types';

const { FIELD_NAMES } = constants;

export function copyDeliveryAddressEffects(deliveryAddress) {
    const fieldCopy = copySecondAddressFields(actions, deliveryAddress);
    return [
        'firstName',
        'middleName',
        'lastName',
        'postalCode',
        'country',
        'line1',
        'line2',
        'pobox',
        'town',
    ].map(fieldCopy);
}

export function* useAlternativeDeliveryAddress({ payload }) {
    if (payload.name === 'addAddress') {
        const defaultAddress = yield select(state => userSelectors.getDefaultAddress(state));
        const deliveryAddress = yield select(state => cartSelectors.getDeliveryAddress(state));
        const cartDeliveryModeCode = yield select(state =>
            cartSelectors.getDeliveryModeCode(state),
        );

        const fieldCopy = copySecondAddressFields(actions, defaultAddress);

        if (deliveryAddress.id === defaultAddress.id || cartDeliveryModeCode === 'pickup') {
            yield all([
                ...['firstName', 'middleName', 'lastName'].map(fieldCopy),
                put(actions.setDeliveryTimeSlots([])),
            ]);
        } else {
            yield all([
                ...copyDeliveryAddressEffects(deliveryAddress),
                call(updateDeliveryTime, {
                    postalCode: deliveryAddress.postalCode,
                    houseNumber: deliveryAddress.line2,
                }),
            ]);
        }
    }
}

function* afterInitialize() {
    yield call(createDeliveryModesLoader, { actions });

    if (!(yield select(userSelectors.isAnonymous))) {
        yield call(checkDeliveryCountry);
        const defaultAddress = yield select(userSelectors.getDefaultAddress);
        const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);
        const cartDeliveryModeCode = yield select(cartSelectors.getDeliveryModeCode);

        if (deliveryAddress.id !== defaultAddress.id && cartDeliveryModeCode !== 'pickup') {
            yield put(actions.addFieldItem({ name: 'addAddress' }));
        }
    }

    const isCheckoutRegistration = yield select(userSelectors.isRegisteredOnCheckout);
    if (isCheckoutRegistration) {
        setRegisteredOnCheckout(true);
    }
}

export function* setFieldsErrors({ payload: e }) {
    const data = yield select(selectors.getData);
    yield call(dynaForms.showErrors, e.json ? e.json.errors : [], data);
}

export function* doSubmit() {
    const deliveryMode = yield select(state => checkoutSelectors.getDeliveryMode(state));

    analytics.handleSelectDeliveryMethod(deliveryMode);

    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(state => cartSelectors.getCartId(state));

    try {
        if (deliveryMode === 'pickup') {
            const selectedStore = yield select(state => checkoutSelectors.getSelectedStore(state));
            yield call(api.setDeliveryMode, userId, cartId, deliveryMode, selectedStore);
        } else {
            const deliveryTime = yield select(state => selectors.getDeliveryTime(state));
            yield call(api.setDeliveryMode, userId, cartId, deliveryMode, null, deliveryTime);
            if (yield select(selectors.useAlternativeDeliveryAddress)) {
                const addressValid = yield call(checkDeliveryAddress);
                if (!addressValid) {
                    return;
                }
                const data = yield select(selectors.getDeliveryAddressFormData);
                const addressDTO = dynaForms.dtoBuilder(data);
                yield call(api.setDeliveryAddress, userId, cartId, addressDTO);
            } else {
                let addressId;
                if (yield select(userSelectors.isAnonymous)) {
                    addressId = yield select(state => cartSelectors.getAnonymousAddress(state).id);
                } else {
                    addressId = yield select(state => userSelectors.getDefaultAddress(state).id);
                }
                yield call(putDeliveryAddress, addressId);
            }
        }
    } catch (e) {
        yield put(actions.setFieldsErrors(e));
        return;
    }

    yield put(cartActions.loadCart());
    yield take(cartTypes.SET_CART_DATA);
    const isCheckoutBlocked = yield select(cartSelectors.isCheckoutBlocked);

    let redirectUrl;
    if (!isCheckoutBlocked) {
        redirectUrl = yield select(selectors.getSuccessRedirectLink);
    } else {
        redirectUrl = yield select(selectors.getFailRedirectLink);
    }

    yield put(globalActions.redirect(redirectUrl));
}

export function* deliveryAddressValidationDone(action) {
    const { name, value, skipValidation } = action.payload;
    if (skipValidation) {
        return;
    }

    if (name === FIELD_NAMES.addAddress && !value) {
        yield call(updateDeliveryTime);
    }
}

export function* checkDeliveryAddress() {
    const isAddressValid = yield select(selectors.isAddressValid);

    if (!isAddressValid) {
        const addressCorrectResponse = yield select(
            cartSelectors.getMessagesBySource,
            'ADDRESS_CONSISTENCY_VALIDATION',
        );
        if (addressCorrectResponse && Object.entries(addressCorrectResponse).length > 0) {
            yield put(uiActions.showModal(types.ADDRESS_VERIFICATION_FAILED_DIALOG));

            const modalAction = yield take([uiTypes.OK_MODAL, uiTypes.CANCEL_MODAL]);

            if (modalAction.type === uiTypes.OK_MODAL) {
                actions.setAddressValidation(true);
            } else {
                return false;
            }
        }
    }
    return true;
}

// Cart is always created with user: {name: Anonymous, ... } so in case of guest checkout we have to wait for the next cart API
// In order to retrive the cart updated with form previously submitted. Otherwise we would rely on old redux state
// which cause postalCode to be null and, with nowhere else to take this information, the API will fail in the try-catch
export function* anonymousAddressSelector() {
    const loading = yield select(cartSelectors.isLoading);
    if (yield select(cartSelectors.getAnonymousAddress) && !loading) {
        return yield select(cartSelectors.getAnonymousAddress);
    }
    yield take(cartTypes.SET_CART_DATA);
    return yield select(cartSelectors.getAnonymousAddress);
}

export function* updateDeliveryTime(data = {}) {
    const selectedDeliveryMode = yield select(checkoutSelectors.getDeliveryMode);
    const isAnonymous = yield select(userSelectors.isAnonymous);

    let deliveryAddress;
    const useAlternativeDeliveryAddress = yield select(selectors.useAlternativeDeliveryAddress);

    if (isAnonymous) {
        deliveryAddress = yield call(anonymousAddressSelector);
    } else {
        const cartDeliveryAddress = yield select(cartSelectors.getDeliveryAddress);
        deliveryAddress = useAlternativeDeliveryAddress
            ? cartDeliveryAddress
            : yield select(userSelectors.getDefaultAddress);
    }

    if (!data.postalCode || !data.line2) {
        data.postalCode = deliveryAddress && deliveryAddress.postalCode;
        data.line2 = deliveryAddress && deliveryAddress.line2;
    }

    let deliveryTimeSlots = [];
    const selectedCountry = deliveryAddress.country.isocode;
    const AddressSuggestionConfig = yield select(configSelectors.getAddressSuggestionConfig);
    const CountrySpecificAddressSuggestionConfig = AddressSuggestionConfig[selectedCountry];

    if (CountrySpecificAddressSuggestionConfig) {
        if (!CountrySpecificAddressSuggestionConfig.sourceFields.every(field => data[field])) {
            yield put(actions.setDeliveryTimeSlots([]));
            yield put(actions.setIsTimeSlotEmpty(true));
            return;
        }
    }

    try {
        deliveryTimeSlots = yield call(api.loadDeliveryTimeSlots, selectedDeliveryMode, {
            postalCode: data.postalCode,
            houseNumber: data.line2,
        });

        yield put(actions.setDataFieldError('deliveryTime', ''));
    } catch (e) {
        yield put(actions.setDataFieldError('deliveryTime', e));
    }

    if (deliveryTimeSlots.length > 0) {
        const formattedDeliveryTimeSlots = deliveryTimeSlots
            .sort((a, b) => {
                return moment(`${a.date} ${a.from}`).diff(moment(`${b.date} ${b.from}`));
            })
            .map(slot => ({
                name: slot.encodedValue,
                label: parseDeliveryTimeSlot(slot),
            }));

        yield put(actions.setDeliveryTimeSlots(formattedDeliveryTimeSlots));
        const deliveryTimeSlot = yield select(state => cartSelectors.getDeliverySlot(state));
        if (deliveryTimeSlots.some(slot => slot.encodedValue === deliveryTimeSlot.encodedValue)) {
            yield put(
                actions.setDataFieldValue('deliveryTime', deliveryTimeSlot.encodedValue, true),
            );
        }
    } else {
        yield put(actions.setDeliveryTimeSlots([]));
    }
}

export function* setDeliveryMode() {
    const deliveryTimeValues = yield select(selectors.getDeliveryTimeValues);
    const useAlternativeDeliveryAddress = yield select(selectors.useAlternativeDeliveryAddress);
    if (!deliveryTimeValues.length && !useAlternativeDeliveryAddress) {
        // Get User Delivery Time in case addAddressForm is not Opened
        yield call(updateDeliveryTime);
    }
}

export function* checkDeliveryCountry() {
    const userCountry = yield select(userSelectors.getUserCountry);
    const currentCountry = yield select(globalSelectors.getCountry);
    if (userCountry !== currentCountry) {
        yield put(checkoutActions.setShippingBlocked(true));
        yield put(actions.addFieldItem({ name: 'addAddress' }));
    } else {
        yield put(checkoutActions.setShippingBlocked(false));
    }
}

export default storeName => formId => [
    ...dynaForms.createSagaFor(storeName, dynaFormsApi.getFormMetadata(formId), doSubmit),
    takeEvery(types.AFTER_INITIALIZE, afterInitialize),
    takeEvery(types.DATA_FIELD_VALIDATION_DONE, deliveryAddressValidationDone),
    takeEvery(types.SET_FIELDS_ERRORS, setFieldsErrors),
    takeEvery(types.ADD_FIELD_ITEM, useAlternativeDeliveryAddress),
    takeEvery(checkoutTypes.SET_DELIVERY_MODE, setDeliveryMode),
    initAddressSuggestionWatchers({ actions, selectors, types }),
];
